import { useCallback, useEffect, useRef, useState } from 'react';
import { Resizable } from 're-resizable';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Badge } from 'primereact/badge';
import { SelectButton } from 'primereact/selectbutton';
import { Tooltip } from 'primereact/tooltip';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  openNotificationsStream,
} from '../../../services/system/NotificationsService';
import { fecthJobs } from '../../../services/accounting/JobsContractsService';
import {
  NotificationRecipient,
  NotificationType,
} from '../../../Interfaces/system/Notifications.interfaces';
import WrapperButton from '../../../components/buttons/WrapperButton';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import UserIcon from '../../../../assets/layout/images/icons/mail.svg';
import BuildingIcon from '../../../../assets/layout/images/icons/building-solid.svg';
import { formatUTCDate } from '@/src/utils/formatUtils';
import useVisibleProcessOptions from '../../../hooks/useVisibleProcessOptions';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useCompanyContext } from '../../../context/CompanyContext';
import { getSource } from '@/src/utils/company';
import { CompanySource } from '@/src/app/Interfaces/admin/User.interfaces';

const typeOptions = [
  { label: 'Action', value: NotificationType.ACTION },
  { label: 'Info', value: NotificationType.INFO },
];

const typeItemTemplate = (option: {
  label: string;
  value: NotificationType;
}) => (
  <span
    className="notif-type-item"
    data-pr-tooltip={option.label}
    data-pr-position="bottom"
  >
    <i
      className={`pi ${
        option.value === NotificationType.ACTION ? 'pi-bolt' : 'pi-info-circle'
      }`}
    />
  </span>
);

const Notification = () => {
  const processOptions = useVisibleProcessOptions();
  const overlayRef = useRef<OverlayPanel>(null);
  const streamRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedCompany } = useCompanyContext();
  const [notifications, setNotifications] = useState<NotificationRecipient[]>(
    []
  );
  const [unreadCount, setUnreadCount] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const filtersRef = useRef({
    companyId: undefined as string | undefined,
    typeFilter: [
      NotificationType.ACTION,
      NotificationType.INFO,
    ] as NotificationType[],
    process: undefined as string | undefined,
    job: undefined as string | undefined,
  });
  const [panelSize, setPanelSize] = useState({ width: 352, height: 480 });
  const [companyFilter, setCompanyFilter] = useState(false);
  const [typeFilter, setTypeFilter] = useState<NotificationType[]>([
    NotificationType.ACTION,
    NotificationType.INFO,
  ]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [processFilter, setProcessFilter] = useState<string | undefined>(
    undefined
  );
  const [jobValue, setJobValue] = useState<string | undefined>(undefined);

  const hasAdvancedFilter =
    processFilter !== undefined || jobValue !== undefined;
  const advancedFilterHeight = advancedOpen ? 107 : 0;
  const itemsPerPage = Math.max(
    5,
    Math.ceil((panelSize.height - 110 - advancedFilterHeight) / 75)
  );

  useQuery({
    queryKey: ['notificationsUnreadCount'],
    queryFn: fetchUnreadCount,
    onSuccess: (count) => setUnreadCount(count),
    refetchOnWindowFocus: false,
  });

  const { data: jobs, isFetching: jobsLoading } = useQuery({
    queryKey: ['notifFilterJobs', selectedCompany?.id],
    queryFn: ({ signal }) =>
      fecthJobs({ companyID: selectedCompany!.id }, signal),
    enabled: (!!selectedCompany && getSource(selectedCompany) !== CompanySource.Yardi),
    refetchOnWindowFocus: false,
  });

  const { fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'notifications',
        companyFilter,
        typeFilter,
        processFilter,
        jobValue,
      ],
      queryFn: ({ pageParam }: { pageParam?: Date }) =>
        fetchNotifications(
          pageParam,
          itemsPerPage,
          companyFilter ? selectedCompany?.id : undefined,
          typeFilter,
          processFilter,
          jobValue
        ),
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.recipients.at(-1)?.createdAt : undefined,
      onSuccess: (result) => {
        const newPage = result.pages.at(-1);
        const isBaseLoad = result.pages.length === 1;
        setNotifications((prev) => {
          if (isBaseLoad) {
            const seen = new Set(prev.map((n) => n.id));
            return [
              ...prev,
              ...(newPage?.recipients ?? []).filter((r) => !seen.has(r.id)),
            ];
          }
          return [...prev, ...(newPage?.recipients ?? [])];
        });
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    });

  const markAsReadRequest = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: (data, args) => {
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === args
            ? { ...notification, readAt: new Date().toISOString() }
            : notification
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    },
  });

  const markAllAsReadRequest = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          readAt: notification.readAt ?? new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    },
  });

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0]?.isIntersecting &&
            hasNextPage &&
            !isFetchingNextPage
          ) {
            fetchNextPage();
          }
        },
        { root: node.parentElement, rootMargin: '120px', threshold: 0 }
      );
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const handleMarkRead = async (recipient: NotificationRecipient) => {
    if (!recipient.readAt) {
      markAsReadRequest.mutate(recipient.id);
    }

    if (recipient.notification.redirectionLink) {
      if (recipient.notification.redirectionLink.includes('http')) {
        window.location.href = recipient.notification.redirectionLink;
      } else {
        navigate(recipient.notification.redirectionLink);
      }

      overlayRef.current?.hide();
    }
  };

  const notificationTemplate = (recipient: NotificationRecipient) => (
    <div
      key={recipient.id}
      onClick={() => handleMarkRead(recipient)}
      className={`mx-1 mb-3 flex gap-2 p-2 border-round cursor-pointer hover:surface-hover shadow-2 ${
        !recipient.readAt ? 'surface-100' : ''
      }`}
    >
      <i
        className={`h-fit my-auto pi ${
          recipient.notification.type === NotificationType.ACTION
            ? 'pi-bolt text-green-500'
            : 'pi-info-circle text-bluwaiBlue'
        }`}
      />
      <div className="flex-grow-1 flex flex-column gap-1 blackText">
        <div className="flex justify-content-between align-items-center gap-2">
          <span className="font-medium text-sm">
            {recipient.notification.title}
          </span>
        </div>
        {recipient.notification.body && (
          <span className="text-color-secondary text-xs">
            {recipient.notification.body}
          </span>
        )}
        <span className="text-color-secondary text-xs ml-auto">
          {formatUTCDate(recipient.createdAt)}
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    const myGeneration = ++generationRef.current;

    const startStream = () => {
      streamRef.current?.abort();
      streamRef.current = openNotificationsStream((incoming) => {
        if (generationRef.current !== myGeneration) return;

        setUnreadCount((c) => c + 1);
        const recipient = incoming as NotificationRecipient;
        const {
          companyId,
          typeFilter: activeTypes,
          process: activeProcess,
          job: activeJob,
        } = filtersRef.current;

        if (!activeTypes.includes(recipient.notification.type)) return;
        if (companyId && recipient.notification.company?.id !== companyId)
          return;
        if (activeProcess && recipient.notification.process !== activeProcess)
          return;
        if (activeJob && recipient.notification.job !== activeJob) return;

        setNotifications((prev) => {
          if (prev.some((n) => n.id === recipient.id)) return prev;

          return [recipient, ...prev];
        });
      });
    };

    startStream();

    return () => {
      generationRef.current = myGeneration + 1;
      streamRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    filtersRef.current = {
      companyId: companyFilter ? selectedCompany?.id : undefined,
      typeFilter,
      process: processFilter,
      job: jobValue,
    };
    queryClient.removeQueries({ queryKey: ['notifications'] });
    setNotifications([]);
  }, [
    companyFilter,
    typeFilter,
    selectedCompany?.id,
    processFilter,
    jobValue,
    queryClient,
  ]);

  return (
    <>
      <WrapperButton
        onClick={(e) => overlayRef.current?.toggle(e)}
        className="relative"
      >
        <img
          src={UserIcon}
          alt="mail icon"
          className="m-2 h-2rem w-3rem lg:h-3rem w-fit"
        />
        {unreadCount > 0 && (
          <Badge
            value={unreadCount > 99 ? '99+' : unreadCount}
            severity="danger"
            className="absolute right-0 top-0 mt-1 mr-1"
          />
        )}
      </WrapperButton>
      <OverlayPanel
        ref={overlayRef}
        className="overlayNoPadding overlayNoArrow"
      >
        <Resizable
          size={panelSize}
          onResizeStop={(_e, _direction, _ref, delta) =>
            setPanelSize((prev) => ({
              width: prev.width + delta.width,
              height: prev.height + delta.height,
            }))
          }
          minWidth={370}
          minHeight={300}
          maxWidth={800}
          enable={{
            right: true,
            bottom: true,
            bottomRight: true,
          }}
          style={{ overflow: 'hidden' }}
          className="p-3"
        >
          <div className="flex justify-content-between align-items-center mb-2 blackText">
            <span className="font-semibold text-lg">Notifications</span>
            {unreadCount > 0 && (
              <WrapperButton
                onClick={() => markAllAsReadRequest.mutate()}
                className="text-md totalColor p-0"
              >
                Mark all as read
              </WrapperButton>
            )}
          </div>
          <div className="flex align-items-stretch justify-content-between mb-2">
            <div className="flex gap-1">
              <Tooltip target=".company-filter-btn" />
              <button
                className={`company-filter-btn p-1 border-round border-1 cursor-pointer ${
                  companyFilter
                    ? 'bluwaiBlue border-primary'
                    : 'surface-0 border-300'
                } p-2`}
                data-pr-tooltip="Selected Company"
                data-pr-position="bottom"
                onClick={() => setCompanyFilter((v) => !v)}
              >
                <img
                  src={BuildingIcon}
                  alt="company"
                  className={`h-1rem w-1rem ${companyFilter ? 'svgWhite' : ''}`}
                />
              </button>
              <Tooltip target=".notif-type-item" />
              <SelectButton
                value={typeFilter}
                options={typeOptions}
                itemTemplate={typeItemTemplate}
                onChange={(e) => e.value?.length && setTypeFilter(e.value)}
                multiple
                className="tabSelectButton smallSpace"
              />
            </div>
            <Tooltip target=".advanced-filter-btn" />
            <button
              className={`advanced-filter-btn border-round border-1 cursor-pointer ${
                advancedOpen
                  ? 'bluwaiBlue text-white border-primary'
                  : 'surface-0 border-300'
              } p-2`}
              data-pr-tooltip="Advanced Filters"
              data-pr-position="bottom"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <i
                className={`pi ${
                  hasAdvancedFilter ? 'pi-filter-fill' : 'pi-filter'
                } ${advancedOpen ? 'text-white' : ''}`}
                style={{ fontSize: '1rem' }}
              />
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateRows: advancedOpen ? '1fr' : '0fr',
              transition: 'grid-template-rows 200ms ease',
            }}
          >
            <div style={{ overflow: 'hidden' }}>
              <div className="flex flex-column gap-2 mb-2 p-2 border-round border-1 border-300 surface-50">
                <div className="flex align-items-center gap-2">
                  <span className="text-sm font-bold text-color-secondary w-4rem flex-shrink-0">
                    Process:
                  </span>
                  <DropdownStyled
                    options={processOptions}
                    labelField="label"
                    valueField="value"
                    value={processFilter}
                    onChange={(e) => setProcessFilter(e.value ?? undefined)}
                    placeholder="Select option"
                    className="flex-1 w-full"
                    fontClass="text-sm"
                  />
                </div>
                <div className="flex align-items-center gap-2">
                  <span className="text-sm font-bold text-color-secondary w-4rem flex-shrink-0">
                    Job:
                  </span>
                  <DropdownStyled
                    options={jobs}
                    labelField={'Job'}
                    valueField={'Job'}
                    value={jobValue}
                    onChange={(e) => setJobValue(e.value ?? undefined)}
                    placeholder="Number"
                    isLoading={jobsLoading}
                    className="flex-1"
                    filter
                    resetFilterOnHide
                    fontClass="text-sm"
                  />
                  <DropdownStyled
                    options={jobs}
                    labelField="Description"
                    valueField={'Job'}
                    value={jobValue}
                    onChange={(e) => setJobValue(e.value ?? undefined)}
                    placeholder="Name"
                    isLoading={jobsLoading}
                    className="flex-1"
                    filter
                    resetFilterOnHide
                    fontClass="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className="dataTableStyled"
            style={{
              maxHeight: `${panelSize.height - 110 - advancedFilterHeight}px`,
              overflowY: 'auto',
            }}
          >
            {notifications.length === 0 ? (
              isFetching ? (
                <ProgressSpinner
                  style={{ width: '2rem', height: '2rem' }}
                  className="flex justify-content-center py-2"
                />
              ) : (
                <div className="text-center text-color-secondary py-3">
                  No notifications
                </div>
              )
            ) : (
              <>
                {notifications.map(notificationTemplate)}
                <div ref={sentinelRef} />
                {isFetchingNextPage && (
                  <ProgressSpinner
                    style={{ width: '2rem', height: '2rem' }}
                    className="flex justify-content-center py-2"
                  />
                )}
              </>
            )}
          </div>
        </Resizable>
      </OverlayPanel>
    </>
  );
};

export default Notification;
